import {
  batchCreateBasicField,
  batchMoveSingleShareToNameSurn,
  batchMoveSurnameShareToNameSurn,
  batchUnlinkSurnameAndCreateNewSurname,
  createFieldValue,
  getNestedOrFloatingElementById,
  getNextUuid,
  batchCreateName,
  batchCreateSurnameWithSharedField,
  isSharedField,
} from '@fs/zion-sls'
import { checkIfHighlightIsShareable } from './highlightUtils'
import { searchForShareableHighlight } from '../../../utils/imageHighlightUtils'

/**
 * Search for a surname that matches text to another field originally associated with that highlight.
 * If found, move any shares at the NAME_SURNAME level to the NAME_SURN level.
 * @param highlights - array from displayHighlights
 * @param fieldId - id of a NAME_SURN field
 * @param value - text desired in the NAME_SURN field
 * @param currentTask - zionSlsState.image
 * @returns {{updatedHighlights, dispatches: *[], sharedField: null}}
 */
export const searchForShareableSurnameAndFixHighShare = (highlights, fieldId, currentTask) => {
  let updatedHighlights = highlights
  let dispatches = []
  const result = checkIfHighlightIsShareable(updatedHighlights, fieldId, currentTask, 'NAME_SURN')
  if (result.sharedField) {
    updatedHighlights = result.updatedHighlights
    const sharedSurnameElId = result.sharedField.superElements?.[0].id
    if (sharedSurnameElId) {
      const sharedSurnameEl = getNestedOrFloatingElementById([currentTask], sharedSurnameElId)
      if (sharedSurnameEl?.superElements?.length > 1) {
        dispatches = dispatches.concat(batchMoveSurnameShareToNameSurn(sharedSurnameEl))
      }
    }
  }
  return { sharedField: result.sharedField, updatedHighlights, dispatches }
}

export const editNameSurn = (
  highlights,
  element,
  value,
  attr,
  unlinkingNames,
  nameElementId,
  zionSLSState,
  currentTask
) => {
  let updatedHighlights = highlights
  const result = searchForShareableSurnameAndFixHighShare(updatedHighlights, element.id, currentTask)
  updatedHighlights = result.updatedHighlights
  let dispatches = result.dispatches
  // Unlink old surname and add new surname
  if (unlinkingNames.NAME_SURN) {
    const unlinkDispatches = batchUnlinkSurnameAndCreateNewSurname({
      nameId: nameElementId,
      surnameField: element,
      images: zionSLSState.images,
      newFieldText: value,
      existingNameSurnId: result.sharedField?.id,
      attribution: attr,
    })
    if (!result.sharedField) {
      // Update highlight field id to the new field if not already updated to shared field
      const createNameSurn = unlinkDispatches.find(
        (disp) => disp.type === 'CREATE_ELEMENT' && disp.value.extraArgs?.fieldType === 'NAME_SURN'
      )
      const newNameSurnId = createNameSurn?.value.id
      updatedHighlights = updatedHighlights.map((h) => {
        if (h.fieldId === element.id) {
          // Save new highlight changes to the new field
          return {
            ...h,
            fieldId: newNameSurnId,
          }
        }
        return h
      })
    }
    // If unlinking the field, we ignore any requests to deselect a highlight
    updatedHighlights = updatedHighlights.map((h) => {
      if (h.change === 'deselected' && h.originalHighlight.fieldId === element.id) {
        return {
          ...h,
          fieldId: h.originalHighlight.fieldId,
          change: null,
        }
      }
      return h
    })
    dispatches = dispatches.concat(unlinkDispatches)
  } else if (result.sharedField) {
    element.superElements.forEach((superEl) => {
      if (superEl.superElements?.length > 1) {
        // if NAME_SURNAME is shared, move the share to NAME_SURN and use sharedFieldId as NAME_SURN
        superEl.superElements.forEach((nameSuperElId) => {
          const nameSuperEl = getNestedOrFloatingElementById(zionSLSState.images, nameSuperElId)
          dispatches = dispatches.concat(
            batchMoveSingleShareToNameSurn(nameSuperEl, superEl, {
              partitionKey: result.sharedField.partitionKey,
              id: result.sharedField.id,
            })
          )
        })
      } else {
        // All NAME_SURNAME containing the NAME_SURN switch to shared field id
        dispatches.push(
          {
            type: 'REM_SUB',
            value: {
              superPartitionKey: superEl.partitionKey,
              subPartitionKey: element.partitionKey,
              superId: superEl.id,
              subId: element.id,
              elementType: 'NAME_SURNAME',
            },
          },
          {
            type: 'ADD_SUB',
            value: {
              superPartitionKey: superEl.partitionKey,
              subPartitionKey: result.sharedField.partitionKey,
              superId: superEl.id,
              subId: result.sharedField.id,
              elementType: 'NAME_SURNAME',
            },
          }
        )
      }
    })
  } else {
    const fieldValue = createFieldValue(value, attr)
    dispatches.push({
      type: 'ADD_FV',
      value: { partitionKey: element.partitionKey, id: element.id, fieldValue },
    })
  }
  return { dispatches, updatedHighlights }
}

export const editShareableSurnameExtension = (
  highlights,
  element,
  value,
  attr,
  unlinkingNames,
  personId,
  zionSLSState
) => {
  const fieldValue = createFieldValue(value, attr)
  let updatedHighlights = highlights
  let dispatches = []
  const result = searchForShareableHighlight(updatedHighlights, element.fieldId, value, zionSLSState.images)

  if (result.sharedField) {
    updatedHighlights = result.updatedHighlights
  }

  if (unlinkingNames[element.fieldType]) {
    const superElement = element.superElements?.find((superEl) => {
      // Get the NAME_SURNAME element that belongs to the current person
      const nameSurname = getNestedOrFloatingElementById(zionSLSState.images, superEl.id)
      return nameSurname.personId === personId
    })
    if (superElement) {
      const newFieldId = getNextUuid()
      dispatches.push({
        type: 'REM_SUB',
        value: {
          superPartitionKey: superElement.partitionKey,
          subPartitionKey: element.partitionKey,
          superId: superElement.id,
          subId: element.id,
          elementType: 'NAME_SURNAME',
        },
      })
      if (result.sharedField) {
        dispatches.push({
          type: 'ADD_SUB',
          value: {
            superPartitionKey: superElement.partitionKey,
            subPartitionKey: result.sharedField.partitionKey,
            superId: superElement.id,
            subId: result.sharedField.id,
            elementType: 'NAME_SURNAME',
          },
        })
      } else {
        dispatches = dispatches.concat(
          batchCreateBasicField({
            partitionKey: element.partitionKey,
            superId: superElement.id,
            fieldType: element.fieldType,
            fieldValue,
            superElementType: 'NAME_SURNAME',
            id: newFieldId,
          })
        )
      }
      // Overwrite unlinked field id in highlights with new field id so highlights are added to it
      updatedHighlights = updatedHighlights.map((h) => {
        if (h.fieldId === element.id) {
          return {
            ...h,
            fieldId: newFieldId,
          }
        }
        return h
      })
    }
    // If unlinking the field, we ignore any requests to deselect a highlight
    updatedHighlights = updatedHighlights.map((h) => {
      if (h.change === 'deselected' && h.originalHighlight.fieldId === element.id) {
        return {
          ...h,
          fieldId: h.originalHighlight.fieldId,
          change: null,
        }
      }
      return h
    })
  } else if (result.sharedField) {
    if (element.superElements) {
      element.superElements.forEach((superEl) => {
        dispatches.push(
          {
            type: 'REM_SUB',
            value: {
              superPartitionKey: superEl.partitionKey,
              subPartitionKey: element.partitionKey,
              superId: superEl.id,
              subId: element.id,
              elementType: 'NAME_SURNAME',
            },
          },
          {
            type: 'ADD_SUB',
            value: {
              superPartitionKey: superEl.partitionKey,
              subPartitionKey: result.sharedField.partitionKey,
              superId: superEl.id,
              subId: result.sharedField.id,
              elementType: 'NAME_SURNAME',
            },
          }
        )
      })
    }
  } else {
    dispatches.push({
      type: 'ADD_FV',
      value: { partitionKey: element.partitionKey, id: element.id, fieldValue },
    })
  }

  return { dispatches, updatedHighlights }
}

const handleSurnameExtensionField = ({
  result,
  surnameElId,
  selectedPerson,
  nameElementId,
  fieldValue,
  fieldType,
  elementType,
  key,
  partitionKey,
  surnamePartitionKey,
}) => {
  let dispatches = []

  if (!surnameElId) {
    if (result.sharedField) {
      dispatches = dispatches.concat(
        batchCreateSurnameWithSharedField({
          partitionKey,
          nameId: nameElementId,
          sharedFieldId: result.sharedField,
        })
      )
    } else {
      dispatches = dispatches.concat(
        batchCreateName({
          partitionKey: selectedPerson.partitionKey,
          nameSuperId: nameElementId,
          fieldValue,
          fieldType,
          elementType,
          nameFieldId: key,
        })
      )
    }
  } else {
    if (isSharedField(surnameElId, [selectedPerson])) {
      const surnameEl = getNestedOrFloatingElementById([selectedPerson], surnameElId)
      dispatches = dispatches.concat(batchMoveSurnameShareToNameSurn(surnameEl))
    }

    const selectedPersonName = selectedPerson.subElements.find((se) => se.elementType === 'NAME')
    const newSurnameId = dispatches.find(
      (disp) => disp.type === 'ADD_SUB' && disp.value.superId === selectedPersonName.id
    )?.value.subId

    if (result.sharedField) {
      dispatches.push({
        type: 'ADD_SUB',
        value: {
          superPartitionKey: surnamePartitionKey,
          subPartitionKey: result.sharedField.partitionKey,
          superId: newSurnameId || surnameElId,
          subId: result.sharedField.id,
          elementType: 'NAME_SURNAME',
        },
      })
    } else {
      dispatches = dispatches.concat(
        batchCreateBasicField({
          partitionKey: surnamePartitionKey,
          superId: newSurnameId || surnameElId,
          fieldType,
          fieldValue,
          superElementType: 'NAME_SURNAME',
          id: key,
        })
      )
    }
  }

  return dispatches
}

export const createName = ({
  highlights,
  key,
  value,
  attr,
  formValues,
  selectedPerson,
  partitionKey,
  surnamePartitionKey,
  surnameElId,
  nameElementId,
  zionSLSState,
}) => {
  let updatedHighlights = highlights
  let dispatches = []

  const fieldType = formValues[`${key}_fieldType`]
  const elementType = {
    NAME_PRE: 'NAME_PREFIX',
    NAME_GN: 'NAME_GIVEN',
    NAME_SURN: 'NAME_SURNAME',
    NAME_SUF: 'NAME_SUFFIX',
    NAME_SPRE: 'NAME_SURNAME',
    NAME_PATR: 'NAME_SURNAME',
    NAME_MATR: 'NAME_SURNAME',
  }[fieldType]

  const result = searchForShareableHighlight(updatedHighlights, key, value, zionSLSState.images)
  if (result.sharedField) {
    updatedHighlights = result.updatedHighlights
  }

  const fieldValue = createFieldValue(value, attr)

  const isSurnameExtension = ['NAME_SPRE', 'NAME_PATR', 'NAME_MATR'].includes(fieldType)

  if (isSurnameExtension) {
    dispatches = dispatches.concat(
      handleSurnameExtensionField({
        result,
        surnameElId,
        selectedPerson,
        nameElementId,
        fieldValue,
        fieldType,
        elementType,
        key,
        partitionKey,
        surnamePartitionKey,
      })
    )
  } else {
    dispatches = dispatches.concat(
      batchCreateName({
        partitionKey: selectedPerson.partitionKey,
        nameSuperId: nameElementId,
        fieldValue,
        fieldType,
        elementType,
        nameFieldId: key,
        nameElementId: elementType === 'NAME_SURNAME' ? surnameElId : getNextUuid(),
      })
    )
  }

  return { dispatches, updatedHighlights }
}
